import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES, type SupportedLocale } from '@kidshome/i18n';
import { cn } from '@kidshome/ui';
import { setLocale } from '@/app/i18n';
import { useAuthStore, authApi } from '@/entities/session';
import { useToast } from '@/shared/ui/toast/toast-context';
import { GlobeIcon } from '@/shared/ui/icons';

/**
 * Reusable, accessible en/uk language switcher (FSD `shared/ui`). A compact segmented `radiogroup`:
 * one radio per supported locale, the active one = `i18n.resolvedLanguage`, selecting one applies the
 * choice optimistically + instantly via `setLocale` (localStorage + `<html lang>`), and — when the
 * caller is signed in — ALSO persists it server-side via the self-service `PUT /v1/auth/me/locale`
 * (so it survives a reload on any device, including for a child/relative who lacks `member:manage`).
 * Persistence is fire-and-forget: on failure we surface a toast but never revert the in-memory
 * switch — the next reload simply falls back to `me.locale`. On the pre-auth screens (no session)
 * persistence is skipped. No hardcoded strings: the group is labelled with `common:language.label`
 * and each option uses `common:language.{locale}`. Semantic theme tokens only, so it renders
 * correctly under both themes / dark mode, with a focus-visible ring.
 *
 * Two surfaces share it:
 * - `variant="compact"` (default) — header-sized, label is the accessible name only (`aria-label`),
 *   for the app/admin shells and the pre-auth screens.
 * - `variant="block"` — a visible heading + the segmented control, for the Account settings card.
 */
export function LanguageToggle({
  variant = 'compact',
  className,
}: {
  variant?: 'compact' | 'block';
  className?: string;
}): React.ReactElement {
  const { t, i18n } = useTranslation(['common', 'auth']);
  const toast = useToast();
  const activeLocale = i18n.resolvedLanguage ?? 'en';
  const label = t('language.label');

  function selectLocale(loc: SupportedLocale): void {
    // 1. Optimistic, instant in-memory switch (works signed-in or not).
    setLocale(loc);
    // 2. When authenticated, persist the choice to the member's profile. Fire-and-forget: mirror it
    //    into the store immediately, and on failure show a toast without reverting the live language.
    const store = useAuthStore.getState();
    if (!store.user) {
      return;
    }
    store.setUserLocale(loc);
    void authApi.persistMyLocale(loc).catch(() => {
      toast.error(t('auth:account.languageSaveError'));
    });
  }

  const group = (
    <div
      role="radiogroup"
      aria-label={variant === 'compact' ? label : undefined}
      aria-labelledby={variant === 'block' ? 'language-toggle-label' : undefined}
      className={cn(
        'inline-flex items-center gap-1 rounded-pill border border-border bg-surface p-0.5',
        variant === 'compact' && className,
      )}
    >
      {SUPPORTED_LOCALES.map((loc: SupportedLocale) => {
        const active = activeLocale === loc;
        return (
          <button
            key={loc}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => selectLocale(loc)}
            className={cn(
              'rounded-pill px-2.5 py-1 text-xs font-semibold uppercase outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
              active
                ? 'bg-primary text-primary-fg'
                : 'text-text-muted hover:bg-surface-muted hover:text-text',
            )}
          >
            <span aria-hidden="true">{loc}</span>
            <span className="sr-only">{t(`language.${loc}` as const)}</span>
          </button>
        );
      })}
    </div>
  );

  if (variant === 'compact') {
    return group;
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <span
        id="language-toggle-label"
        className="flex items-center gap-1.5 text-sm font-medium text-text"
      >
        <GlobeIcon size={15} className="text-text-muted" />
        {label}
      </span>
      {group}
    </div>
  );
}
